import { supabase } from '@/lib/supabase';
import { isAfter, parseISO, subMonths } from 'date-fns';
import * as Sentry from '@sentry/react';

const MAX_BOOKINGS_STANDARD = 200;

export interface CreateBookingParams {
    bookingData: any;
    isPro: boolean;
    currentBookings: any[];
}

export const createBookingService = async ({
    bookingData,
    isPro,
    currentBookings
}: CreateBookingParams) => {
    try {
        if (
            !isPro &&
            currentBookings.filter((b) => b.status === 'confirmed' && isAfter(parseISO(b.created_at), subMonths(new Date(), 1))).length >= MAX_BOOKINGS_STANDARD // controlla limite massimo di 200 prenotazione al mese
        ) {
            throw new Error(`Limite prenotazioni raggiunto.`);
        }

        const cleanData = Object.fromEntries(
            Object.entries(bookingData).filter(([_, v]) => v != null && v !== '')
        );

        const { data, error } = await supabase
            .from('Bookings')
            .insert(cleanData)
            .select()
            .single();

        if (error) throw error;

        return { success: true, data, error: '' };
    } catch (error: any) {
        if (error?.message !== 'Limite prenotazioni raggiunto.') {
            Sentry.captureException(error, {
                // Servono per la classificazione degli errori su Sentry
                tags: {
                    feature: 'bookings',
                    action: 'create',
                    plan_type: isPro ? 'pro' : 'standard'
                },
                // Servono per avere più info sull'errore
                extra: {
                    bookingData,
                    isPro,
                    totalCurrentBookings: currentBookings.length
                }
            });
        }

        return {
            success: false,
            error: error?.message || 'Errore creazione prenotazione',
        };
    }
};

export const updateBookingService = async (oldBookingId: number | string, bookingData: any) => {
    try {
        const { error } = await supabase
            .from('Bookings')
            .update(bookingData)
            .eq('id', oldBookingId);

        if (error) {
            Sentry.captureException(error, {
                tags: { feature: 'bookings', action: 'update' },
                extra: { oldBookingId, bookingData }
            });
            // I trigger del DB (fn_check_booking_validity) sollevano messaggi in italiano
            // già pensati per l'utente: mostrarli invece del generico "Errore aggiornamento."
            return { success: false, error: error.message || 'Errore aggiornamento.' };
        }

        // Se bookingData contiene 'status' è un soft delete: inviare notifica di cancellazione
        if (bookingData?.status === 'cancelled') {
            // Chiamata alla Edge Function per l'invio dell'email di cancellazione
            try {
                const { data, error: functionError } = await supabase.functions.invoke('on-booking-cancellation', {
                    body: { bookingId: oldBookingId }
                });

                const errorMessage = data?.message || '';

                if (errorMessage.includes('Il cliente non ha una mail associata')) {
                    // Potresti mostrare un avviso all'utente
                } else if (functionError) {
                    console.error("Errore Edge Function cancellation:", functionError);
                    Sentry.captureException(functionError, {
                        tags: { feature: 'bookings', action: 'cancellation_notification' },
                        extra: { bookingId: oldBookingId }
                    });
                }
            } catch (err) {
                console.error("Invio notifica cancellazione fallito:", err);
                Sentry.captureException(err, {
                    tags: { feature: 'bookings', action: 'cancellation_notification_exception' },
                    extra: { bookingId: oldBookingId }
                });
            }
        } else { // Altrimenti si tratta di una modifica: inviare notifica di aggiornamento
            // Chiamata alla Edge Function per l'invio dell'email di aggiornamento
            try {
                const { data, error: functionError } = await supabase.functions.invoke('on-booking-updated', {
                    body: {
                        bookingId: oldBookingId
                    }
                });

                const errorMessage = data?.message || '';

                if (errorMessage.includes('Il cliente non ha una mail associata')) {
                    // Potresti mostrare un avviso all'utente
                } else if (functionError) {
                    console.error("Errore Edge Function update:", functionError);
                    Sentry.captureException(functionError, {
                        tags: { feature: 'bookings', action: 'update_notification' },
                        extra: { oldBookingId, bookingData }
                    });
                }
            } catch (err) {
                console.error("Invio notifica update fallito:", err);
                Sentry.captureException(err, {
                    tags: { feature: 'bookings', action: 'update_notification_exception' },
                    extra: { oldBookingId, bookingData }
                });
            }
        }

        return { success: true, error: '' };
    } catch (error: any) {
        Sentry.captureException(error, {
            tags: { feature: 'bookings', action: 'update_exception' },
            extra: { oldBookingId, bookingData }
        });
        return { success: false, error: 'Errore imprevisto update.' };
    }
};

export const deleteBookingService = async (bookingId: number | string) => {
    try {
        const { error } = await supabase
            .from('Bookings')
            .delete()
            .eq('id', bookingId);

        if (error) {
            Sentry.captureException(error, {
                tags: { feature: 'bookings', action: 'delete' },
                extra: { bookingId }
            });
            throw error;
        }

        return { success: true };
    } catch (error: any) {
        Sentry.captureException(error, {
            tags: { feature: 'bookings', action: 'delete_exception' },
            extra: { bookingId }
        });
        return { success: false, error: 'Errore eliminazione.' };
    }
};
